import { ReactNode, useState } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, Button, IconButton, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ClearIcon from '@mui/icons-material/Clear';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';
import { useAppSelector, useAppDispatch } from 'app/store';
import {
  getCardProductApplication,
  isDocumentFileUnreadableError,
  isDocumentSessionStrandedError,
  isDocumentUploadFailedError,
  selectReviewOnboardSlice,
  sendDocumentForManualReview,
} from 'app/pages/store/reviewOnboardSlice';
import { MAX_RECEIPT_SIZE, validMimeTypes } from 'app/shared-components/receipts/ReceiptConstants';
import {
  HNAccountHolderVerification,
  HNAccountHolderVerificationStatusCode,
  HNApplicationDocumentUploadSessionStatus,
  HNDocumentType,
} from 'API';
import { useTranslation } from 'react-i18next';
import { sentryMask } from 'sentryReplayPrivacy';

enum DocumentType {
  primaryAuthorizedPerson = 'primaryAuthorizedPerson',
  businessProfile = 'businessProfile',
  beneficialOwner = 'beneficialOwner',
}

interface Props {
  currentVerification?: HNAccountHolderVerification | null;
  array: HNDocumentType[];
  type: DocumentType;
}

interface FileArgs {
  id: string;
  file: File | undefined;
  documentType?: HNDocumentType;
  currentVerification?: HNAccountHolderVerification | null;
}

// The picker's `accept` has to carry extensions as well as MIME types. On the same
// Windows machines where File.type comes back empty — no registered handler for the
// extension — the OS resolves a MIME-only accept list through that same registry, so
// a MIME-only filter greys out the very PDF the File.type fallback exists to allow.
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf'];
const ACCEPTED_EXTENSIONS_RE = /\.(jpe?g|png|pdf)$/i;
const FILE_PICKER_ACCEPT = [...validMimeTypes, ...ACCEPTED_EXTENSIONS].join(',');

function AdditionalDocuments() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { application, integration } = useAppSelector(selectReviewOnboardSlice);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<{ [key: string]: FileArgs[] }>({});
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const businessRequired =
    application?.accountHolderSnapshot?.businessProfile?.currentVerification?.reason ===
    'DOCUMENT_UPLOAD_REQUIRED';

  const individualRequired =
    application?.accountHolderSnapshot?.primaryAuthorizedPerson?.currentVerification?.reason ===
    'DOCUMENT_UPLOAD_REQUIRED';

  const beneficialOwnersRequired =
    application?.accountHolderSnapshot?.businessProfile?.ultimateBeneficialOwners?.some(
      (owner) => owner?.currentVerification?.reason === 'DOCUMENT_UPLOAD_REQUIRED'
    );

  const filterBusinessReasonsRequiredDocuments: HNDocumentType[] = (
    application?.accountHolderSnapshot?.businessProfile?.currentVerification?.requiredDocuments || []
  ).reduce((accumulator: HNDocumentType[], document) => {
    if (document?.documentUploadSession?.primaryDocumentTypes?.length) {
      accumulator.push(...document.documentUploadSession.primaryDocumentTypes);
    }
    return accumulator;
  }, []);

  const filterIndividualReasonsRequiredDocuments: HNDocumentType[] = (
    application?.accountHolderSnapshot?.primaryAuthorizedPerson?.currentVerification?.requiredDocuments || []
  ).reduce((accumulator: HNDocumentType[], document) => {
    if (document && document.documentUploadSession && document.documentUploadSession.primaryDocumentTypes) {
      accumulator.push(...document.documentUploadSession.primaryDocumentTypes);
    }
    return accumulator;
  }, []);

  const filterBeneficialOwnersReasonsRequiredDocuments = (
    application?.accountHolderSnapshot?.businessProfile?.ultimateBeneficialOwners || []
  )
    .filter((owner) => owner != null)
    .map((owner) => {
      return {
        owner,
        documents: (owner?.currentVerification?.requiredDocuments || []).reduce(
          (accumulator: HNDocumentType[], document) => {
            if (document?.documentUploadSession?.primaryDocumentTypes?.length) {
              accumulator.push(...document.documentUploadSession.primaryDocumentTypes);
            }
            return accumulator;
          },
          []
        ),
      };
    });

  // Only a session still in CREATED accepts uploads. Returns undefined rather than
  // asserting, so a verification whose session has already been started renders an
  // explanation instead of throwing mid-render (SEE-1594).
  const findSessionId = (currentVerification?: HNAccountHolderVerification | null) =>
    (currentVerification?.requiredDocuments || []).find(
      (session) => session?.status === HNApplicationDocumentUploadSessionStatus.CREATED
    )?.documentUploadSession?.id ?? undefined;

  const findFile = (document: HNDocumentType, currentVerification?: HNAccountHolderVerification | null) => {
    const sessionId = findSessionId(currentVerification);
    if (!sessionId) return undefined;
    return (files[sessionId] || []).find((file) => file.id === document);
  };

  const normalizeDocumentName = (document: HNDocumentType) => document.replaceAll('_', ' ');

  // File.type is '' on plenty of real Windows setups where the extension is not
  // registered, so a genuine PDF would otherwise be rejected outright.
  const hasAcceptedType = (picked: File) =>
    picked.type ? validMimeTypes.includes(picked.type) : ACCEPTED_EXTENSIONS_RE.test(picked.name);

  const handleChange = (file: FileArgs) => {
    const sessionId = findSessionId(file.currentVerification);
    const picked = file.file;
    if (!sessionId || !picked) return;

    if (!hasAcceptedType(picked)) {
      enqueueSnackbar(t('invalidFileType'), { variant: 'error' });
      return;
    }
    if (picked.size === 0) {
      enqueueSnackbar(
        t('additionalDocuments.fileEmpty', 'That file is empty. Please choose a different one.'),
        { variant: 'error' }
      );
      return;
    }
    if (picked.size > MAX_RECEIPT_SIZE) {
      enqueueSnackbar(t('transactionSidesheet.fileTooLarge'), { variant: 'error' });
      return;
    }

    // Kept synchronous on purpose. An await here would leave the hidden <input>
    // mounted until setFiles landed, and two change events inside that window
    // both append, uploading the same document type twice. Readability is proven
    // at submit time instead, where it actually matters.
    setFiles((oldValues) => {
      return { ...oldValues, [sessionId]: [...(oldValues[sessionId] || []), file] };
    });
  };

  const handleRemove = (id: string, currentVerification?: HNAccountHolderVerification | null) => {
    const sessionId = findSessionId(currentVerification);
    if (!sessionId) return;
    setFiles((oldValues) => {
      return {
        ...oldValues,
        [sessionId]: [...(oldValues[sessionId] || []).filter((file) => file.id !== id)],
      };
    });
  };

  const attachedSessionIds = Object.keys(files).filter((sessionId) =>
    (files[sessionId] || []).some((file) => file.file)
  );

  const sendFiles = async () => {
    if (attachedSessionIds.length === 0) return;

    setLoading(true);
    try {
      // .unwrap() is required: a rejected thunk still resolves the dispatch promise,
      // which is how upload failures used to pass for success.
      const results = await Promise.allSettled(
        attachedSessionIds.map((sessionId) =>
          dispatch(
            sendDocumentForManualReview({
              documentUploadSessionId: sessionId,
              files: files[sessionId],
            })
          ).unwrap()
        )
      );

      const reasons = results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map((result) => result.reason);

      // Report by severity, not by attach order. A stranded session needs support
      // and must not be hidden behind another session's re-selectable file.
      const stranded = reasons.find(isDocumentSessionStrandedError);
      const uploadFailed = reasons.find(isDocumentUploadFailedError);
      const unreadable = reasons.find(isDocumentFileUnreadableError);

      if (stranded) {
        // Deliberately does not say "try again": the session is IN_PROGRESS, so
        // Highnote has emptied the document list and a retry cannot clear it.
        enqueueSnackbar(
          t(
            'additionalDocuments.sessionStranded',
            "We couldn't complete your document submission. Please contact support so we can finish it for you."
          ),
          { variant: 'error' }
        );
      } else if (uploadFailed) {
        // Also does not say "try again". The session was closed with a partial
        // set, so the document list is now empty and the page will re-render into
        // the "already being processed" state.
        enqueueSnackbar(
          t(
            'additionalDocuments.uploadPartiallyFailed',
            "Some of your documents didn't upload. We've submitted the ones that did — please contact support to send the rest."
          ),
          { variant: 'error' }
        );
      } else if (unreadable) {
        enqueueSnackbar(
          t(
            'additionalDocuments.fileUnreadableRetry',
            "We couldn't read one of your files — it may have moved or changed since you selected it. Please remove it, select it again and resubmit."
          ),
          { variant: 'error' }
        );
      } else if (reasons.length > 0) {
        enqueueSnackbar(
          t(
            'additionalDocuments.uploadFailed',
            "We couldn't submit your documents. Please try again, or contact support if this keeps happening."
          ),
          { variant: 'error' }
        );
      } else {
        enqueueSnackbar(t('additionalDocuments.submitted', 'Documents submitted for review.'), {
          variant: 'success',
        });
      }

      // Keep only the sessions that are still genuinely retryable — the ones that
      // failed a pre-start read, where nothing reached Highnote and re-selecting
      // the file works. Everything else is spent: a fulfilled session is SUBMITTED,
      // a partial failure ended the session too (that is the point of the change),
      // and a stranded one cannot be cleared by retrying. Leaving any of those in
      // `files` keeps Submit Files enabled under the "already being processed"
      // text, and a second click restarts a closed session and reports a bogus
      // DocumentSessionStrandedError('start') to Sentry.
      const retryableSessionIds = new Set(
        attachedSessionIds.filter((_, index) => {
          const result = results[index];
          return result.status === 'rejected' && isDocumentFileUnreadableError(result.reason);
        })
      );
      const spentSessionIds = attachedSessionIds.filter((id) => !retryableSessionIds.has(id));
      if (spentSessionIds.length > 0) {
        setFiles((oldValues) => {
          const next = { ...oldValues };
          spentSessionIds.forEach((sessionId) => delete next[sessionId]);
          return next;
        });
      }

      // Only skip the refetch when nothing reached Highnote at all — every
      // rejection was a pre-start read failure and no session succeeded.
      const nothingReachedHighnote =
        reasons.length === results.length && reasons.every(isDocumentFileUnreadableError);
      if (integration && !nothingReachedHighnote) {
        // Highnote needs a moment to reflect the submitted session on the application.
        await new Promise((resolve) => setTimeout(resolve, 10000));
        await dispatch(getCardProductApplication(integration));
      }
    } finally {
      // Previously only reached inside `if (integration)`, so a missing integration
      // left the button spinning forever.
      setLoading(false);
    }
  };

  const WrapList = (props: { list: ReactNode[] | undefined }) => (
    <List sx={{ ml: 2.5, pt: 0 }}>
      {props.list?.map((text, i) => (
        <ListItem
          key={`listItem-${i}`}
          sx={{
            mt: 0,
            padding: 0,
            fontSize: '12px',
            listStyleType: 'disc',
            display: 'list-item',
            fontWeight: 400,
          }}
        >
          {text}
        </ListItem>
      ))}
    </List>
  );
  const WrapperDocuments = ({ array, currentVerification, type }: Props) => {
    // Highnote only lists the acceptable document types while the upload session is
    // CREATED; once started it returns an empty list. Rendering nothing here left
    // the user staring at a page with no way to attach anything (SEE-1594).
    //
    // Gated on a session actually existing rather than on an empty list: a
    // verification that is pending for some reason other than documents has no
    // requiredDocuments at all, and telling that customer their request is "being
    // processed" would be a fabrication.
    if (array.length === 0) {
      const hasStartedSession = (currentVerification?.requiredDocuments || []).some(
        (required) => required?.documentUploadSession?.id
      );
      if (!hasStartedSession) return null;

      return (
        <Typography sx={{ my: 3, color: '#4F4F4F', fontWeight: 400, fontSize: '1.6rem' }}>
          {t(
            'additionalDocuments.noDocumentOptions',
            'This document request is already being processed. If you still need to upload something, please contact support.'
          )}
        </Typography>
      );
    }

    return (
      <Grid container gap={3} sx={{ my: 3 }}>
        {array.map((document, i) => {
          const find = findFile(document, currentVerification);
          return (
            <Grid key={`${type}-${i}`}>
              <Paper
                elevation={4}
                style={{
                  padding: 14,
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '250px',
                  height: '300px',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 500,
                    mb: 0.5,
                  }}
                >
                  {t('uploadDocument')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 400,
                  }}
                >
                  {t('attachImageOfDocument')}
                </Typography>
                <Box display="flex">
                  <WrapList list={[<strong>{normalizeDocumentName(document)}</strong>]} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexGrow: 1,
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                  }}
                >
                  {find ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        backgroundColor: theme.palette.primary.lighter,
                        borderRadius: 5,
                        px: 2.5,
                        py: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          mr: 3,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            WebkitLineClamp: 1,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {find?.file?.name}
                        </Typography>
                        <span>{find?.file?.size} KB</span>
                      </Box>
                      <IconButton
                        aria-label="upload picture"
                        component="label"
                        onClick={() => handleRemove(document, currentVerification)}
                      >
                        <ClearIcon sx={{ color: 'black' }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button variant="contained" component="label" sx={{ mb: 1 }}>
                      {t('chooseFile')}
                      <input
                        hidden
                        accept={FILE_PICKER_ACCEPT}
                        multiple
                        type="file"
                        onChange={(e) =>
                          handleChange({
                            id: document,
                            file: e.target.files?.[0],
                            documentType: document,
                            currentVerification,
                          })
                        }
                      />
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box>
      <Box>
        <Typography
          variant="h1"
          sx={{
            mb: 2,
            color: 'primary.main',
            fontWeight: 400,
          }}
        >
          {t('additionalInformationNeeded')}
        </Typography>
        <Typography
          variant="h3"
          sx={{
            mb: 0.5,
            color: '#4F4F4F',
            fontWeight: 600,
          }}
        >
          {t('additionalDocumentsNeeded')}
        </Typography>
        <Typography
          sx={{
            color: '#4F4F4F',
            fontWeight: 400,
            fontSize: '1.6rem',
          }}
        >
          {t('acceptedFileTypes')}
        </Typography>
      </Box>
      {individualRequired ? (
        <Box>
          <Typography
            variant="h2"
            sx={{
              mt: 5,
              color: '#4F4F4F',
              fontWeight: 400,
            }}
          >
            {t('identityDocumentation')}
          </Typography>
          <WrapperDocuments
            currentVerification={
              application?.accountHolderSnapshot?.primaryAuthorizedPerson?.currentVerification
            }
            array={filterIndividualReasonsRequiredDocuments}
            type={DocumentType.primaryAuthorizedPerson}
          />
        </Box>
      ) : null}

      {businessRequired ? (
        <Box>
          <Typography
            variant="h2"
            sx={{
              mt: 5,
              color: '#4F4F4F',
              fontWeight: 400,
            }}
          >
            {t('businessEntityDocumentation')}
          </Typography>
          <WrapperDocuments
            currentVerification={application?.accountHolderSnapshot?.businessProfile?.currentVerification}
            array={filterBusinessReasonsRequiredDocuments}
            type={DocumentType.businessProfile}
          />
        </Box>
      ) : null}

      {beneficialOwnersRequired ? (
        <Box>
          <Typography
            variant="h2"
            sx={{
              mt: 5,
              color: '#4F4F4F',
              fontWeight: 400,
            }}
          >
            {t('uboEntityDocumentation')}
          </Typography>
          {filterBeneficialOwnersReasonsRequiredDocuments
            .filter(
              (item) =>
                item.owner?.currentVerification?.status === HNAccountHolderVerificationStatusCode.PENDING
            )
            .map((item, i) => (
              <Box key={`owner-${i}`}>
                {/* An `h3` is unmasked chrome, so the marker sits on the heading itself. */}
                <Typography
                  variant="h3"
                  sx={{
                    mt: 2,
                    color: '#4F4F4F',
                    fontWeight: 400,
                  }}
                  {...sentryMask}
                >
                  {item.owner?.name?.givenName + ' ' + item.owner?.name?.familyName}
                </Typography>
                <WrapperDocuments
                  currentVerification={item.owner?.currentVerification}
                  array={item.documents}
                  type={DocumentType.beneficialOwner}
                />
              </Box>
            ))}
        </Box>
      ) : null}
      <Box sx={{ display: 'flex', mt: 10 }}>
        <LoadingButton
          loading={loading}
          // Was always enabled: with nothing attached it spun for the 10s refetch
          // delay and then silently did nothing (SEE-1594).
          disabled={attachedSessionIds.length === 0}
          variant="contained"
          startIcon={<SendIcon />}
          onClick={() => sendFiles()}
        >
          {t('submitFiles')}
        </LoadingButton>
      </Box>
    </Box>
  );
}

export default AdditionalDocuments;
